import { useState, useMemo } from 'react';

import PropTypes from 'prop-types';
import { BsPlus } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import Card from '#globalComponents/Card/Card';

import { useCustomFieldDefs } from '../../../models/customFields/queries';
import CustomFieldsSection from '../../common/CustomFieldsSection/CustomFieldsSection';
import ContactRow from '../ContactRow/ContactRow';

const ContactsCard = ({
	contacts,
	companyId,
	aircallConnected,
	onCreateContact,
	onUpdateContact,
	onDeleteContact,
	onAircallDial,
	highlightedContactId,
}) => {
	const [creating, setCreating] = useState(false);
	const [draft, setDraft] = useState({
		firstName: '',
		lastName: '',
		companyRole: '',
		emailAddress: '',
		phoneNumber: '',
		customFields: {},
	});
	const [submitting, setSubmitting] = useState(false);

	const { data: contactFieldDefs = [] } = useCustomFieldDefs('contact');

	const hasRequiredFieldsMissing = useMemo(() => {
		return contactFieldDefs.some((def) => {
			if (!def.isRequired) return false;
			if (def.isDefault) {
				const val = draft[def.fieldKey];
				return !val || (typeof val === 'string' && !val.trim());
			}
			const val = draft.customFields[def.id];
			return val === undefined || val === null || val === '';
		});
	}, [contactFieldDefs, draft]);

	const resetDraft = () =>
		setDraft({ firstName: '', lastName: '', companyRole: '', emailAddress: '', phoneNumber: '', customFields: {} });

	const handleCreate = async () => {
		if (!draft.firstName?.trim() && !draft.lastName?.trim() && !draft.emailAddress?.trim()) return;
		if (hasRequiredFieldsMissing) return;
		setSubmitting(true);
		try {
			const cf = Object.fromEntries(
				Object.entries(draft.customFields).filter(([, v]) => v !== '' && v !== undefined),
			);
			await onCreateContact({
				crmCompanyId: companyId,
				firstName: draft.firstName?.trim() || null,
				lastName: draft.lastName?.trim() || null,
				companyRole: draft.companyRole?.trim() || null,
				emailAddress: draft.emailAddress?.trim() || null,
				phoneNumber: draft.phoneNumber?.trim() || null,
				salutation: draft.salutation || null,
				linkedinUrl: draft.linkedinUrl?.trim() || null,
				...(Object.keys(cf).length > 0 ? { customFields: cf } : {}),
			});
			setCreating(false);
			resetDraft();
		} finally {
			setSubmitting(false);
		}
	};

	const hasMinInput = draft.firstName?.trim() || draft.lastName?.trim() || draft.emailAddress?.trim();

	return (
		<Card className="overflow-hidden p-0">
			<div className="px-4 py-2.5 border-b border-bgBase-500 bg-bgBase-300 flex items-center justify-between">
				<h4 className="text-xs-head text-text">
					Kontakte{' '}
					<span className="text-bgBase-800 font-normal normal-case tracking-normal">· {contacts.length}</span>
				</h4>
				<button
					type="button"
					onClick={() => {
						resetDraft();
						setCreating(true);
					}}
					className="inline-flex items-center gap-1 text-[11px] text-bgBase-800 hover:text-text transition-colors"
				>
					<BsPlus className="w-3.5 h-3.5" /> Kontakt
				</button>
			</div>

			{creating && (
				<div className="border-b-2 border-primary-400 bg-primary-100 px-4 py-3 flex flex-col gap-2 animate-[fadeIn_0.15s_ease]">
					<div className="flex items-center gap-2 text-[12px] font-semibold text-text">
						<BsPlus className="w-3 h-3 text-primary-600" />
						Neuer Kontakt
					</div>
					<CustomFieldsSection
						entityType="contact"
						entity={draft}
						values={draft.customFields}
						inline
						showLabel={false}
						onChangeEntity={(fieldKey, value) => setDraft((d) => ({ ...d, [fieldKey]: value }))}
						onChange={(fieldId, value) =>
							setDraft((d) => ({ ...d, customFields: { ...d.customFields, [fieldId]: value } }))
						}
					/>
					<div className="flex gap-1.5 pt-1.5 justify-end border-t border-primary-200">
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() => {
								setCreating(false);
								resetDraft();
							}}
						>
							Abbrechen
						</Button>
						<Button
							type="button"
							variant="solid"
							size="sm"
							onClick={handleCreate}
							disabled={!hasMinInput || hasRequiredFieldsMissing || submitting}
							loading={submitting}
						>
							Kontakt anlegen
						</Button>
					</div>
				</div>
			)}

			{contacts.length === 0 && !creating ? (
				<p className="px-4 py-4 text-xs text-bgBase-800 italic">Keine Kontakte</p>
			) : (
				contacts.map((c) => (
					<ContactRow
						key={c.id}
						contact={c}
						highlighted={c.id === highlightedContactId}
						onUpdate={onUpdateContact}
						onDelete={onDeleteContact}
						aircallConnected={aircallConnected}
						onAircallDial={onAircallDial}
					/>
				))
			)}
		</Card>
	);
};

ContactsCard.propTypes = {
	contacts: PropTypes.array.isRequired,
	companyId: PropTypes.string.isRequired,
	aircallConnected: PropTypes.bool.isRequired,
	onCreateContact: PropTypes.func.isRequired,
	onUpdateContact: PropTypes.func.isRequired,
	onDeleteContact: PropTypes.func.isRequired,
	onAircallDial: PropTypes.func.isRequired,
	highlightedContactId: PropTypes.string,
};

export default ContactsCard;
